import { Center, Text } from '@chakra-ui/react';
import { distance } from '@turf/turf';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

type ScaleControlProps = {
  zoom: number | undefined;
  maxWidth?: number;
};

function getRoundNum(num: number) {
  const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
  let d = num / pow10;

  d = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : 1;

  return pow10 * d;
}

function ScaleControl({ zoom = 14, maxWidth = 100 }: Readonly<ScaleControlProps>) {
  const { current: mapbox } = useMap();
  const [width, setWidth] = useState<number>(maxWidth);
  const [value, setValue] = useState<string | null>(null);

  const setScale = (maxWidth: number, maxDistance: number) => {
    let unit = 'm';
    let distance = getRoundNum(maxDistance);
    const ratio = distance / maxDistance;

    if (distance >= 1000) {
      distance = distance / 1000;
      unit = 'km';
    }

    setWidth(maxWidth * ratio);
    setValue(distance + unit);
  };

  useEffect(() => {
    if (mapbox) {
      const y = mapbox.getContainer().clientHeight / 2;
      const src = mapbox.unproject([0, y]);
      const target = mapbox.unproject([maxWidth, y]);
      const maxMeters = distance([src.lng, src.lat], [target.lng, target.lat], { units: 'meters' });
      setScale(maxWidth, maxMeters);
    }
  }, [mapbox, maxWidth, zoom]);

  return (
    <Center
      backgroundColor="neutral.800-op70"
      color="neutral.white"
      width={maxWidth + 10}
      height={{ base: '32px', '4k': '40px' }}
    >
      <Text position="absolute" top={0.25}>
        {value}
      </Text>
      <svg xmlns="http://www.w3.org/2000/svg" width={maxWidth} height="8" viewBox={`0 0 ${width} 9`} fill="none">
        <path d={`M0 0 L0 8 H${width} V 0`} stroke="white" strokeWidth="2" />
      </svg>
    </Center>
  );
}

export default ScaleControl;
